<template>
  <div id="reply-box" class="p-2">
    <div class="border border-gray-300 bg-white rounded-lg shadow-sm">
      <div class="flex items-center p-2">
        <button @click="setMessageMode('reply')" class="mr-3 px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-gray-900"  :class="{ 'text-gray-900 bg-gray-100 hover:bg-gray-200': messageMode === 'reply', 'text-gray-500 hover:text-gray-900 bg-white hover:bg-gray-100': messageMode !== 'reply' }">
          {{ $t('conversation.sendReply') }}
        </button>
        <button @click="setMessageMode('internal')" class="px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-gray-900"   :class="{ 'text-gray-900 bg-gray-100 hover:bg-gray-200': messageMode === 'internal', 'text-gray-500 hover:text-gray-900 bg-white hover:bg-gray-100': messageMode !== 'internal' }">
          {{ $t('conversation.writeInternal') }}
        </button>
      </div>
      <div class="flex-grow relative">
        <div v-if="showContextMenu" class="absolute context-menu bg-white border border-gray-300 rounded-md text-sm max-h-64 overflow-y-scroll scrollbar-thin scrollbar-thumb-indigo-300 scrollbar-track-indigo-50" :style="{'top': cannedMessagesWidgetOffset}">
          <div v-for="message of whatsappMessages" v-bind:key="message.id" class="border-b border-gray-300 hover:bg-indigo-200">
            <WhatsappTemplateMessage :message="message" v-on:template-selected="sendWhatsappMessageTemplate" />
          </div>
          <div v-for="message of cannedMessages" v-bind:key="message.id" class="border-b border-gray-300 p-2 hover:bg-indigo-200">
            <CannedMessage :message="message" v-on:template-selected="insertMessageTemplate" />
          </div>
        </div>
      </div>
      <div class="p-2 relative">
        <template v-if="blockNormalMessageMode && !canScheduleMessage">
          <span @click="showContextMenuForWhatsapp" id="text" role="textbox" type="text" class="text-input text-gray-600 mt-2 mb-2 block w-full whitespace-pre max-h-36 overflow-y-scroll overflow-x-hidden p-2 pb-2 pt-2 whitespace-pre-line break-words  scrollbar-thin scrollbar-thumb-indigo-300 scrollbar-track-indigo-50">
            {{messageInputPlaceholder}}
          </span>
        </template>
        <template v-else>
          <span v-if="!text" class="absolute top-7 left-4 pointer-events-none  text-gray-600">{{messageInputPlaceholder}}</span>
          <span id="text" role="textbox" type="text" class="text-input mt-2 mb-2 block w-full whitespace-pre max-h-36 overflow-y-scroll overflow-x-hidden p-2 pb-2 pt-2 whitespace-pre-line break-words  scrollbar-thin scrollbar-thumb-indigo-300 scrollbar-track-indigo-50" v-on:keyup="updateText" contenteditable></span>
        </template>
      </div>
      <div class="border-t border-gray-200 px-2 py-2 flex justify-between items-center space-x-3 sm:px-3">
        <div class="flex">
          <button type="button" class="-ml-2 -my-2 rounded-full px-3 py-2 inline-flex items-center text-left text-gray-400 group overflow-hidden relative" :class="{'cursor-default': blockNormalMessageMode, 'cursor-pointer': !blockNormalMessageMode}">
            <svg class="-ml-1 h-5 w-5 mr-2 group-hover:text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd" d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z" clip-rule="evenodd"></path>
            </svg>
            <span class="text-sm text-gray-500 group-hover:text-gray-600 italic">{{ $t('actions.attach') }}</span>
            <input v-if="!blockNormalMessageMode || canScheduleMessage" class="z-20 p-5 top-0 left-0 opacity-0 absolute" type="file" multiple="multiple" :accept="acceptedAttachments" @change="saveAttachment"  :class="{'cursor-default': blockNormalMessageMode && !canScheduleMessage, 'cursor-pointer': !blockNormalMessageMode || canScheduleMessage}" />
          </button>
        </div>
        <div class="flex-shrink-0">
          <template v-if="blockNormalMessageMode && !canScheduleMessage">
            <ChannelSelect v-on:channel-selected="channelSelected" :selectedChannel="channel" :deletedChannels="deletedChannels" :channels="customerChannels" :conversationId="conversationId" />
            <button type="button" class="opacity-25 ml-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-600 cursor-default">
              {{ $t('actions.send') }}
            </button>
          </template>
          <template v-else>
            <template v-if="messageMode === 'reply' && !conversationTimeWindowExpired">
              <ChannelSelect v-on:channel-selected="channelSelected" :selectedChannel="channel" :deletedChannels="deletedChannels" :channels="customerChannels" :conversationId="conversationId" />
            </template>
            <button v-on:click="sendMessageToCustomer" type="button" class="ml-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
              <template v-if="canScheduleMessage">
                {{ $t('actions.schedule') }}
              </template>
              <template v-else>
                {{ $t('actions.send') }}
              </template>
            </button>
          </template>
        </div>
      </div>
      <div v-if="attachments.length > 0">
        <div v-for="attachment of attachments" v-bind:key="attachment" class="relative inline-block m-2 p-1 border border-gray-300 rounded-md">
          <AttachmentPreview :attachment="attachment" v-on:remove-attachment="removeAttachment" />
        </div>
      </div>
    </div>
  </div>
  <FileToLargeModal v-if="showFileSizeModal" v-on:close-file-modal="closeFileSizeModal" :size="maxSizeInMB" />
</template>


<script>

import ChannelSelect from "./ChannelSelect";
import AttachmentPreview from "./AttachmentPreview";
import CannedMessage from "./CannedMessage";
import WhatsappTemplateMessage from "./WhatsappTemplateMessage";
import FileToLargeModal from "./FileToLargeModal";
import dayjs from "dayjs";

import { useCannedMessagesStore } from '@/stores/cannedMessages';
import { useWhatsappMessagesStore } from '@/stores/whatsappMessageTemplates';
import { useCustomerChannelStore } from '@/stores/customerChannels';


export default {
  name: 'ReplyBox',
  emits: ['set-size-after', 'channel-selected'],
  props: ['draggedFiles', 'conversationTimeWindowExpired', 'canScheduleMessage', 'deletedChannels', 'allChannelsDeleted', 'conversationId', 'conversation', 'whatsappContextMenuCloseRequest', 'whatsappContextMenuOpenRequest', 'customerChannels'],
  components: {
    ChannelSelect,
    AttachmentPreview,
    CannedMessage,
    WhatsappTemplateMessage,
    FileToLargeModal,
  },
  created() {
    this.watchForPasteEvents();
    this.setChannelFromDefault();
  },
  setup() {
    let whatsappMessagesStore = useWhatsappMessagesStore();
    let cannedMessagesStore = useCannedMessagesStore();
    let customerChannelStore = useCustomerChannelStore();
    return {
      cannedMessagesStore,
      whatsappMessagesStore,
      customerChannelStore,
    };
  },
  methods: {
    watchForPasteEvents() {
      let self = this;
      setTimeout(function () {
        let widget = document.getElementById('text');
        if (!widget) {
          setTimeout(function () {
            self.watchForPasteEvents();
          }, 100);
          return;
        }
        widget.onpaste = function(pasteEvent) {
          self.setSizeAfter();
          let item = pasteEvent.clipboardData.items[0];
          if (item.type.indexOf("image") === 0) {
            let blob = item.getAsFile();
            let filename = item.type.replace('/', '.').replace('image', Date.now().toString());
            self.attachments.push({'file': blob, 'name': filename, 'type': item.type});
            return false;
          }
          let widget = document.getElementById('text');
          widget.innerText =  widget.textContent + pasteEvent.clipboardData.getData('text/plain');
          self.text = widget.innerText;

          const selection = window.getSelection();
          const range = document.createRange();
          selection.removeAllRanges();
          range.selectNodeContents(widget);
          range.collapse(false);
          selection.addRange(range);
          widget.focus();
          return false;
        }
      }, 0);
    },
    channelSelected(channel) {
      this.channelId = channel.id;
      this.channel = channel;
      this.$emit('channel-selected', channel);
    },
    updateText(e) {
      this.manageIsTyping();
      this.showContextMenu = false;
      let input = document.getElementById('text');
      if (input) {
          this.text = input.innerText.trim();
          if (this.text.startsWith('/')) {
            this.showContextMenu = this.shouldShowContextMenu;
          }
      }
      if (e.keyCode === 13 && !e.shiftKey) {
        this.sendMessageToCustomer();
      }
      this.setSizeAfter();
    },
    closeFileSizeModal() {
      this.showFileSizeModal = false;
    },
    insertMessageTemplate(messageTemplate) {
      this.text = messageTemplate.attributes.text;
      this.showContextMenu = false;
      let input = document.getElementById('text');
      if (input) {
        input.innerText = messageTemplate.attributes.text;
      }
      if (messageTemplate.attributes['media-uri']) {
        this.attachments.push({
          'url': messageTemplate.attributes['media-uri'],
          'name': messageTemplate.attributes['media-name'],
          'type': messageTemplate.attributes['media-type']}
        );
      }
      this.setSizeAfter();
    },
    sendWhatsappMessageTemplate(template) {
      this.$root.messageEmit('send_whatsapp_templated_message_to_customer', this.$root.token, template.id, this.conversationId, this.channelId);
      let input = document.getElementById('text');
      if (input) {
        input.innerText = '';
      }
      this.showContextMenu = false;
    },
    removeAttachment(attachment) {
      let index = this.attachments.indexOf(attachment);
      if (index !== -1) {
        this.attachments.splice(index, 1);
        this.setSizeAfter();
      }
    },
    setMessageMode(mode) {
      this.messageMode = mode;
      if (this.conversationTimeWindowExpired) {
        this.watchForPasteEvents();
      }
    },
    saveAttachment(event) {
      let attachments = Array.from(event.target.files);
      return this.parseAttachmentFiles(attachments);
    },
    parseAttachmentFiles(attachments) {
      let maxSize = process.env.VUE_APP_MAX_ATTACHMENT_SIZE_IN_MB * 1024 * 1024;
      let extensions = this.allowedExtensions;
      attachments.forEach(file => {
        if(file.size >= maxSize) {
          this.showFileSizeModal = true;
        } else {
          let check = extensions.filter(e => {
            return file.name.indexOf(e) !== -1;
          });
          if (check.length > 0) {
            this.attachments.push({'file': file, 'name': file.name, 'type': file.type});
          }
        }
      });
      this.setSizeAfter();
    },
    sendMessageToCustomer() {
      if (this.text || this.attachments) {
        if (this.messageMode === 'reply') {
          let isScheduled = this.conversationTimeWindowExpired && this.canScheduleMessage;
          if (isScheduled) {
            this.$root.messageEmit('schedule_message_to_customer', this.$root.token, this.text, this.conversationId, this.channelId, this.attachments);
          } else {
            this.$root.messageEmit('send_message_to_customer', this.$root.token, this.text, this.conversationId, this.channelId, this.attachments);
          }
        } else {
          this.$root.messageEmit('save_internal_note', this.$root.token, this.text, this.conversationId, this.attachments);
        }
        this.text = '';
        this.attachments = [];
        document.getElementById('text').innerText = '';
        this.setSizeAfter();
      }
    },
    manageIsTyping() {
      let agentId = this.$root.currentAgent.id;
      if (!this.isTyping[agentId] || dayjs().diff(this.isTyping[agentId], 'second') > 2) {
        this.isTyping[agentId] = new Date();
        this.$root.businessEmit('agent_typing', this.$root.token, this.conversationId);
      }
    },
    showContextMenuForWhatsapp() {
      if (this.isWhatsappChannel && this.whatsappMessages.length > 0) {
        setTimeout(() => {
          this.showContextMenu = true;
        }, 0);
      }
    },
    setSizeAfter() {
      this.$emit('set-size-after');
    },
    setChannelFromDefault() {
      if (this.recentChannel) {
        this.channelId = this.recentChannelId;
        this.channel = this.recentChannel;
        this.$emit('channel-selected', this.channel);
      }
    }
  },
  computed: {
    shouldShowContextMenu() {
      return this.cannedMessages.length > 0 || this.whatsappMessages.length > 0;
    },
    cannedMessagesWidgetOffset() {
      let totalLength = this.cannedMessages.length + this.whatsappMessages.length;
      if (totalLength > 4) {
        totalLength = 4;
      }
      let offset = totalLength * 77;
      return `-${offset}px`;
    },
    cannedMessages() {
      if (this.conversationTimeWindowExpired) {
        return [];
      }
      if (this.text.length > 1) {
        let searchTerm = this.text.replace('/', '');
        return this.cannedMessagesStore.filterByPhrase(searchTerm);
      }
      return this.cannedMessagesStore.activeTemplates();
    },
    whatsappMessages() {
      if (!this.conversationTimeWindowExpired || !this.isWhatsappChannel) {
        return [];
      }
      if (this.text.length > 1) {
        let searchTerm = this.text.replace('/', '');
        return this.whatsappMessagesStore.filterByPhrase(searchTerm);
      }
      return this.whatsappMessagesStore.activeTemplates();
    },
    isWhatsappChannel() {
      return this.channel && this.channel.attributes.type === 'whatsapp';
    },
    maxSizeInMB: function () {
      return process.env.VUE_APP_MAX_ATTACHMENT_SIZE_IN_MB;
    },
    acceptedAttachments: function() {
      if (this.channel && this.channel.attributes.type === 'instagram') {
        return '.png,.jpg,.gif';
      }
      return '.mp4,.xls,.xlsx,.pdf,.doc,.docx,.odt,.odf,.ods,.odp,.txt,.png,.jpg,.gif';
    },
    allowedExtensions: function() {
      if (this.channel && this.channel.attributes.type === 'instagram') {
        return ['.png', '.jpg', '.gif'];
      }
      return ['.mp4', '.xls', '.xlsx', '.pdf', '.doc', '.docx', '.odt', '.odf', '.ods', '.odp', '.txt', '.png', '.jpg', '.gif'];
    },
    recentChannelId() {
      if (this.conversation.latest_customer_message) {
        return this.conversation.latest_customer_message.customer_channel_id;
      }
      return '';
    },
    recentChannel() {
      if (this.recentChannelId) {
        return this.customerChannelStore.getById(this.recentChannelId);
      }
      return '';
    },
    messageInputPlaceholder() {
      if (this.messageMode === 'internal') {
        return '';
      } else if (this.allChannelsDeleted) {
        return this.$t('conversation.allChannelsDeleted');
      } else if (this.isWhatsappChannel && this.whatsappMessages.length > 0) {
        if (this.canScheduleMessage) {
          return this.$t('conversation.scheduleMessage');
        } else {
          return this.$t('conversation.clickToPickWhatsappTemplate');
        }
      } else if (this.conversationTimeWindowExpired) {
        return this.$t('conversation.timeWindowExpired');
      } else {
        return this.$t('conversation.writeMessagePlaceholder');
      }
    },
    blockNormalMessageMode() {
      return (this.allChannelsDeleted || this.conversationTimeWindowExpired) && this.messageMode === 'reply';
    },
  },
  watch: {
    conversationId: function(newId) {
      if (newId) {
        this.isTyping = {};
        this.attachments = [];
        this.setChannelFromDefault();
      }
    },
    recentChannel: function(channel) {
      if (channel) {
        this.setChannelFromDefault();
      }
    },
    whatsappContextMenuOpenRequest(newValue) {
      if (newValue) {
        this.showContextMenuForWhatsapp();
      }
    },
    whatsappContextMenuCloseRequest(newValue) {
      if (newValue) {
        this.showContextMenu = false;
      }
    },
    draggedFiles(newFiles) {
      if (newFiles) {
        let attachments = Array.from(newFiles);
        return this.parseAttachmentFiles(attachments);
      }
    },
  },
  data() {
    return {
      text: '',
      channelId: undefined,
      channel: undefined,
      messageMode: 'reply',
      attachments: [],
      showFileSizeModal: false,
      showContextMenu: false,
      isTyping: {},
    }
  },
}
</script>
